import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MathText, HtmlWithEquations } from "../../components/MathText";
import { QUESTION_TYPE_LABELS, formatMatchAnswer, matchLabel } from "../../utils/questionTypes";
import {
  deleteQuestionAPI,
  exportQuestionsCsv,
  fetchSectionWithQuestionsAPI,
  fetchSectionsAPI,
  moveQuestionAPI,
  reorderQuestionsAPI,
  storageUrl,
} from "../../utils/api";
import { showError, showSuccess, showDeleteConfirmation } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState, Modal, SearchField } from "../../components/ui";
// CSV import is disabled on the section questions page for now
// import ImportQuestionsModal from "../../components/ImportQuestionsModal";
// import ImportHistoryModal from "../../components/ImportHistoryModal";
// import { downloadSampleCsv } from "../../utils/questionImportSample";
import "./ViewQuestions.css";

const OPTION_TYPES = ["SINGLE_CORRECT", "MCQ", "MULTI_CORRECT", "PARAGRAPH"];

const parseAnswerKeys = (answer) => {
  if (!answer) return [];
  const str = String(answer);
  return (str.includes(",") ? str.split(",") : str.split("")).map((s) => s.trim().toUpperCase()).filter(Boolean);
};

const getMatchPairs = (question) => {
  if (question.correct_matches && typeof question.correct_matches === "object") {
    return Object.entries(question.correct_matches);
  }
  const fromOptions = (question.options || [])
    .filter((o) => o.match_key && /^[P-U]$/.test(o.option_key))
    .map((o) => [o.match_key, o.option_key])
    .sort(([a], [b]) => String(a).localeCompare(String(b)));
  return fromOptions;
};

function ViewQuestions() {
  const navigate = useNavigate();
  const { seriesId, sectionId } = useParams();

  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [section, setSection] = useState(null);
  const [error, setError] = useState("");
  const [viewLanguage, setViewLanguage] = useState("english");
  const [modalImage, setModalImage] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  // const [importOpen, setImportOpen] = useState(false);
  // const [historyOpen, setHistoryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const addQuestionPath = `/admin/test-series/${seriesId}/section/${sectionId}/add-question`;
  const isEnglish = viewLanguage === "english";

  const fetchQuestions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetchSectionWithQuestionsAPI(sectionId);
      if (!response) return;
      if (!response.success) throw new Error(response.message || "Failed to load section");

      const sectionData = response.data;
      if (!sectionData) throw new Error("No section data returned");

      setSection(sectionData);
      // Already in the section's order (set with the arrows below)
      setQuestions(sectionData.questions || []);
    } catch (err) {
      console.error("Error fetching section:", err);
      setError(`Couldn't load questions: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionId) fetchQuestions();
  }, [sectionId, fetchQuestions]);

  useEffect(() => {
    if (!modalImage) return undefined;
    const onKey = (e) => e.key === "Escape" && setModalImage(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalImage]);

  const handleDeleteQuestion = async (questionId, number) => {
    const result = await showDeleteConfirmation("question", `Question ${number}`);
    if (!result.isConfirmed) return;

    try {
      setDeletingId(questionId);
      const response = await deleteQuestionAPI(questionId);
      if (!response) return;
      // 409: students have answered it; the message says what to do instead
      if (!response.success) throw new Error(response.message || "The server rejected the request");
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
      showSuccess(`Question ${number} was removed from this section.`, "Question deleted");
    } catch (err) {
      console.error("Error deleting question:", err);
      showError(`Couldn't delete the question: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Move a question up (-1) or down (+1) in the section; saved straight away
  const [reordering, setReordering] = useState(false);
  const moveInOrder = async (index, delta) => {
    const target = index + delta;
    if (reordering || target < 0 || target >= questions.length) return;

    const previous = questions;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    setQuestions(next);
    setReordering(true);

    try {
      const response = await reorderQuestionsAPI(parseInt(sectionId, 10), next.map((q) => q.id));
      if (response && !response.success) {
        setQuestions(previous);
        showError(response.message || "Couldn't save the new order.");
      }
    } catch {
      setQuestions(previous);
      showError("Couldn't save the new order. Please try again.");
    } finally {
      setReordering(false);
    }
  };

  // Move or copy a question to another section of this test
  const [moving, setMoving] = useState(null); // { question, number }
  const [moveSections, setMoveSections] = useState([]);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveCopy, setMoveCopy] = useState(false);
  const [moveSaving, setMoveSaving] = useState(false);

  const openMove = async (question, number) => {
    setMoving({ question, number });
    setMoveTarget("");
    setMoveCopy(false);
    const response = await fetchSectionsAPI(seriesId);
    if (response?.success) setMoveSections(response.data || []);
  };

  const handleMove = async () => {
    if (!moveTarget || !moving) return;
    setMoveSaving(true);
    try {
      const response = await moveQuestionAPI(moving.question.id, Number(moveTarget), moveCopy);
      if (!response) return;
      if (!response.success) {
        showError(response.message || "Couldn't move the question.");
        return;
      }
      showSuccess(response.message, moveCopy ? "Question copied" : "Question moved");
      setMoving(null);
      if (!moveCopy) {
        setQuestions((prev) => prev.filter((q) => q.id !== moving.question.id));
      } else if (Number(moveTarget) === Number(sectionId)) {
        fetchQuestions();
      }
    } finally {
      setMoveSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportQuestionsCsv({ sectionId });
    } catch (err) {
      showError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const hasHindiContent = questions.some((q) => q.question_hi || q.questionHindi);
  const typesPresent = Array.from(new Set(questions.map((q) => q.question_type).filter(Boolean)));

  const query = search.trim().toLowerCase();
  const visibleQuestions = questions
    .map((q, index) => ({ q, number: index + 1 }))
    .filter(({ q }) => typeFilter === "all" || q.question_type === typeFilter)
    .filter(({ q }) => {
      if (!query) return true;
      const haystack = [q.question_en, q.question_hi, q.topic_name, q.topic_name_hi, q.paragraph_en]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });

  const target = Number(section?.total_questions) || 0;
  // Reordering works on the full list; hide the arrows while a search or filter hides some questions
  const canReorder = !query && typeFilter === "all" && questions.length > 1;

  const openImage = (path) => setModalImage(storageUrl(path));

  const renderThumb = (path, alt) =>
    path ? (
      <button type="button" className="vq-thumb" onClick={() => openImage(path)} title="Click to enlarge">
        <img src={storageUrl(path)} alt={alt} loading="lazy" />
        <span>
          <i className="bi bi-arrows-fullscreen"></i>
        </span>
      </button>
    ) : null;

  return (
    <div className="view-questions page">
      {modalImage && (
        <div className="vq-lightbox" onClick={() => setModalImage(null)} role="dialog" aria-modal="true">
          <button type="button" className="vq-lightbox-close" onClick={() => setModalImage(null)} aria-label="Close">
            <i className="bi bi-x-lg"></i>
          </button>
          <img src={modalImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* {importOpen && (
        <ImportQuestionsModal
          sectionId={sectionId}
          scopeLabel={section?.name}
          onClose={() => setImportOpen(false)}
          onImported={fetchQuestions}
        />
      )}

      {historyOpen && (
        <ImportHistoryModal
          sectionId={sectionId}
          scopeLabel={section?.name}
          onClose={() => setHistoryOpen(false)}
          onChanged={fetchQuestions}
        />
      )} */}

      <div className="page-header">
        <div>
          <button
            type="button"
            className="back-link"
            onClick={() => navigate(`/admin/test-series/${seriesId}/sections`)}
          >
            <i className="bi bi-arrow-left"></i> Sections
          </button>
          <h2>
            {section?.parent?.name ? `${section.parent.name} › ` : ""}
            {section?.name || "Section questions"}
          </h2>
          <p>
            {isLoading
              ? "Loading…"
              : `${questions.length}${target ? ` of ${target}` : ""} question${questions.length === 1 ? "" : "s"} added`}
          </p>
        </div>
        <div className="header-actions">
          {hasHindiContent && (
            <div className="segmented" role="tablist" aria-label="Language">
              <button type="button" className={isEnglish ? "active" : ""} onClick={() => setViewLanguage("english")}>
                English
              </button>
              <button type="button" className={!isEnglish ? "active" : ""} onClick={() => setViewLanguage("hindi")}>
                हिंदी
              </button>
            </div>
          )}
          {/* <button type="button" className="btn-ghost" onClick={() => downloadSampleCsv()}>
            <i className="bi bi-file-earmark-spreadsheet"></i> Sample CSV
          </button>
          <button type="button" className="btn-ghost" onClick={() => setHistoryOpen(true)}>
            <i className="bi bi-clock-history"></i> Import history
          </button>
          <button type="button" className="btn-secondary" onClick={() => setImportOpen(true)}>
            <i className="bi bi-upload"></i> Import CSV
          </button> */}
          <button
            type="button"
            className="btn-secondary"
            onClick={handleExport}
            disabled={questions.length === 0 || exporting}
          >
            {exporting ? <span className="btn-spinner"></span> : <i className="bi bi-download"></i>} Export CSV
          </button>
          <Link to={addQuestionPath} className="btn-primary">
            <i className="bi bi-plus-lg"></i> Add question
          </Link>
        </div>
      </div>

      <ErrorAlert message={error} onRetry={fetchQuestions} />

      {isLoading ? (
        <LoadingState message="Loading questions…" />
      ) : questions.length === 0 ? (
        !error && (
          <EmptyState
            icon="bi bi-question-square"
            title="No questions yet"
            message="This section is empty. Add the first question to get started."
            action={
              <div className="vq-empty-actions">
                <Link to={addQuestionPath} className="btn-primary">
                  <i className="bi bi-plus-lg"></i> Add first question
                </Link>
                {/* <button type="button" className="btn-secondary" onClick={() => setImportOpen(true)}>
                  <i className="bi bi-upload"></i> Import CSV
                </button>
                <button type="button" className="btn-ghost" onClick={() => downloadSampleCsv()}>
                  <i className="bi bi-file-earmark-spreadsheet"></i> Download sample CSV
                </button> */}
              </div>
            }
          />
        )
      ) : (
        <>
          <div className="toolbar">
            <SearchField value={search} onChange={setSearch} placeholder="Search question text or topic…" />
            {typesPresent.length > 1 && (
              <div className="chip-group">
                <button
                  type="button"
                  className={`chip ${typeFilter === "all" ? "active" : ""}`}
                  onClick={() => setTypeFilter("all")}
                >
                  All <span className="chip-count">{questions.length}</span>
                </button>
                {typesPresent.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`chip ${typeFilter === type ? "active" : ""}`}
                    onClick={() => setTypeFilter(type)}
                  >
                    {QUESTION_TYPE_LABELS[type] || type}
                    <span className="chip-count">{questions.filter((q) => q.question_type === type).length}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {visibleQuestions.length === 0 ? (
            <EmptyState
              icon="bi bi-search"
              title="No matching questions"
              message="Try a different search term or filter."
              action={
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setSearch("");
                    setTypeFilter("all");
                  }}
                >
                  Clear filters
                </button>
              }
            />
          ) : (
            <div className="vq-list">
              {visibleQuestions.map(({ q: question, number }) => {
                const type = question.question_type;
                const correctKeys = parseAnswerKeys(question.correct_answer || question.correct_option);
                const questionText = isEnglish
                  ? question.question_en || question.questionEng
                  : question.question_hi || question.questionHindi;
                const explanation = isEnglish
                  ? question.explanation_en || question.explanationEng
                  : question.explanation_hi || question.explanationHindi;
                const paragraph = isEnglish ? question.paragraph_en : question.paragraph_hi;
                const paragraphImg = isEnglish ? question.paragraph_en_image : question.paragraph_hi_image;
                const options = [...(question.options || [])].sort((a, b) =>
                  String(a.option_key).localeCompare(String(b.option_key))
                );

                return (
                  <article key={question.id} className="vq-card card">
                    <header className="vq-card-header">
                      <div className="vq-card-tags">
                        <span className="vq-number">Q{number}</span>
                        {type && <span className="badge badge-brand">{QUESTION_TYPE_LABELS[type] || type}</span>}
                        {question.marks != null && question.marks !== "" && (
                          <span className="badge badge-success">+{question.marks}</span>
                        )}
                        {Number(question.negative_marks) > 0 && (
                          <span className="badge badge-danger">−{question.negative_marks}</span>
                        )}
                        {(question.topic_name || question.topic_name_hi) && (
                          <span className="badge badge-neutral">
                            <i className="bi bi-tag"></i>{" "}
                            {[question.topic_name, question.topic_name_hi].filter(Boolean).join(" / ")}
                          </span>
                        )}
                      </div>
                      <div className="row-actions">
                        {canReorder && (
                          <>
                            <button
                              type="button"
                              className="btn-ghost btn-icon btn-sm"
                              onClick={() => moveInOrder(number - 1, -1)}
                              disabled={number === 1 || reordering}
                              title="Move up"
                              aria-label={`Move question ${number} up`}
                            >
                              <i className="bi bi-arrow-up"></i>
                            </button>
                            <button
                              type="button"
                              className="btn-ghost btn-icon btn-sm"
                              onClick={() => moveInOrder(number - 1, 1)}
                              disabled={number === questions.length || reordering}
                              title="Move down"
                              aria-label={`Move question ${number} down`}
                            >
                              <i className="bi bi-arrow-down"></i>
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="btn-ghost btn-icon btn-sm"
                          onClick={() => openMove(question, number)}
                          title="Move or copy to another section"
                          aria-label="Move or copy to another section"
                        >
                          <i className="bi bi-folder-symlink"></i>
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() =>
                            navigate(
                              `/admin/test-series/${seriesId}/section/${sectionId}/edit-question/${question.id}`
                            )
                          }
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          type="button"
                          className="btn-danger btn-icon btn-sm"
                          onClick={() => handleDeleteQuestion(question.id, number)}
                          disabled={deletingId === question.id}
                          title="Delete question"
                          aria-label="Delete question"
                        >
                          {deletingId === question.id ? (
                            <span className="btn-spinner"></span>
                          ) : (
                            <i className="bi bi-trash3"></i>
                          )}
                        </button>
                      </div>
                    </header>

                    <div className="vq-card-body">
                      {type === "PARAGRAPH" && (paragraph || paragraphImg) && (
                        <div className="vq-paragraph">
                          <span className="vq-label">Passage</span>
                          {paragraph ? (
                            <HtmlWithEquations html={paragraph} />
                          ) : null}
                          {renderThumb(paragraphImg, "Passage")}
                        </div>
                      )}

                      <div className="vq-question">
                        <MathText
                          text={questionText}
                          empty={
                            <span className="vq-missing">
                              {isEnglish ? "No question text" : "हिंदी में प्रश्न उपलब्ध नहीं है"}
                            </span>
                          }
                        />
                        {renderThumb(question.image, "Question")}
                      </div>

                      {OPTION_TYPES.includes(type) && options.length > 0 && (
                        <div className="vq-options">
                          {options.map((option) => {
                            const isCorrect = correctKeys.includes(String(option.option_key).toUpperCase());
                            const optionImg = isEnglish
                              ? option.option_en_image || option.image
                              : option.option_hi_image;
                            return (
                              <div key={option.id ?? option.option_key} className={`vq-option ${isCorrect ? "correct" : ""}`}>
                                <span className="vq-option-key">{option.option_key}</span>
                                <div className="vq-option-body">
                                  <MathText
                                    text={isEnglish ? option.option_en : option.option_hi || option.option_en}
                                    empty={<span className="vq-missing">—</span>}
                                  />
                                  {renderThumb(optionImg, `Option ${option.option_key}`)}
                                </div>
                                {isCorrect && <i className="bi bi-check-circle-fill vq-option-check"></i>}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {type === "MATCH" && options.length > 0 && (
                        <>
                          <div className="vq-match">
                            {[
                              { title: "Column I", keys: /^[A-F]$/ },
                              { title: "Column II", keys: /^[P-U]$/ },
                            ].map((col) => (
                              <div key={col.title} className="vq-match-col">
                                <span className="vq-label">{col.title}</span>
                                {options
                                  .filter((o) => col.keys.test(String(o.option_key)))
                                  .map((option) => (
                                    <div key={option.id ?? option.option_key} className="vq-option">
                                      <span className="vq-option-key">{matchLabel(option.option_key)}</span>
                                      <div className="vq-option-body">
                                        <MathText
                                          text={isEnglish ? option.option_en : option.option_hi || option.option_en}
                                        />
                                        {renderThumb(
                                          isEnglish ? option.option_en_image : option.option_hi_image || option.option_en_image,
                                          `${col.title} ${matchLabel(option.option_key)}`
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ))}
                          </div>
                          {(getMatchPairs(question).length > 0 || question.correct_answer) && (
                            <div className="vq-answer">
                              <i className="bi bi-check2-circle"></i>
                              <span>Correct matches</span>
                              <div className="vq-pairs">
                                {getMatchPairs(question).length > 0 ? (
                                  getMatchPairs(question).map(([a, b]) => (
                                    <span key={`${a}-${b}`} className="badge badge-success">
                                      {matchLabel(a)} → {matchLabel(b)}
                                    </span>
                                  ))
                                ) : (
                                  <strong>{formatMatchAnswer(question.correct_answer)}</strong>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {type === "NUMERICAL" && question.numerical_value != null && question.numerical_value !== "" && (
                        <div className="vq-answer">
                          <i className="bi bi-check2-circle"></i>
                          <span>Answer</span>
                          <strong>{question.numerical_value}</strong>
                        </div>
                      )}

                      {type === "NUMERICAL" &&
                        (question.numerical_value == null || question.numerical_value === "") &&
                        Array.isArray(question.numerical_ranges) &&
                        question.numerical_ranges.length > 0 && (
                          <div className="vq-answer">
                            <i className="bi bi-check2-circle"></i>
                            <span>Accepted {question.numerical_ranges.length === 1 ? "range" : "ranges"}</span>
                            <strong>
                              {question.numerical_ranges
                                .map((r) => (r && typeof r === "object" ? `${r.min} to ${r.max}` : String(r)))
                                .join(", ")}
                            </strong>
                          </div>
                        )}

                      {type === "RANGE" && question.correct_answer && (
                        <div className="vq-answer">
                          <i className="bi bi-check2-circle"></i>
                          <span>Accepted range</span>
                          <strong>{question.correct_answer}</strong>
                        </div>
                      )}

                      {(explanation || question.youtube_link) && (
                        <details className="vq-explanation">
                          <summary>
                            <i className="bi bi-lightbulb"></i> Solution
                            <i className="bi bi-chevron-down vq-caret"></i>
                          </summary>
                          <div className="vq-explanation-body">
                            {explanation && <MathText text={explanation} />}
                            {question.youtube_link && (
                              <a
                                href={question.youtube_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="vq-youtube"
                              >
                                <i className="bi bi-youtube"></i> Watch video solution
                                <i className="bi bi-box-arrow-up-right"></i>
                              </a>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}

      <Modal
        open={Boolean(moving)}
        onClose={() => !moveSaving && setMoving(null)}
        icon="bi bi-folder-symlink"
        title={moving ? `Move or copy question ${moving.number}` : ""}
        subtitle="Choose a section of this test."
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setMoving(null)} disabled={moveSaving}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={handleMove} disabled={!moveTarget || moveSaving}>
              {moveSaving && <span className="btn-spinner"></span>} {moveCopy ? "Copy question" : "Move question"}
            </button>
          </>
        }
      >
        <div className="segmented vq-move-mode" role="radiogroup" aria-label="Move or copy">
          <button type="button" className={!moveCopy ? "active" : ""} onClick={() => setMoveCopy(false)}>
            <i className="bi bi-arrow-right"></i> Move
          </button>
          <button type="button" className={moveCopy ? "active" : ""} onClick={() => setMoveCopy(true)}>
            <i className="bi bi-files"></i> Copy
          </button>
        </div>
        <p className="vq-move-hint">
          {moveCopy
            ? "A copy (with its options and images) is added at the end of the chosen section. This question stays here."
            : "The question leaves this section and is added at the end of the chosen section."}
        </p>
        <div className="form-group">
          <label htmlFor="move-target">Section</label>
          <select id="move-target" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}>
            <option value="">Choose a section…</option>
            {moveSections
              // Subjects hold sections, not questions
              .filter((s) => !s.is_subject)
              .filter((s) => moveCopy || String(s.id) !== String(sectionId))
              .map((s) => {
                const subject = s.parent_id ? moveSections.find((p) => p.id === s.parent_id) : null;
                return (
                  <option key={s.id} value={s.id}>
                    {subject ? `${subject.name} › ${s.name}` : s.name}
                    {String(s.id) === String(sectionId) ? " (this section)" : ""} · {s.questions_count ?? 0} questions
                  </option>
                );
              })}
          </select>
        </div>
      </Modal>
    </div>
  );
}

export default ViewQuestions;
